import { parseBaseUrlsData } from '../../utils';
import { keyBy } from 'lodash';
const DS_SERVER_BASE = 'https://apps.wix.com';
const dsServerUrl = (siteHeaderUrl, useAbsolutePath = false) => `${useAbsolutePath ? DS_SERVER_BASE : ''}/document-services-server/v2/query/apps/${siteHeaderUrl}`;
const BLOCKS_APP_DESCRIPTOR_SITE_ASSET_MODULE_VERSION = '1.73.0';
const DATA_FIXER_VERSION = '1.3187.0';
export const BLOCKS_APP_DESCRIPTOR_SITE_ASSET_MODULE_BASE_URL = 'https://siteassets.parastorage.com/pages/pages/pages/blocksAppDescriptor';
const token = 'token';
const SANTA_DATA_FIXER = '@wix/santa-data-fixer';
const BLOCKS_APP_DESCRIPTOR = '@wix/blocks-app-descriptor';
export const getAppDescriptorSiteAssetModuleUrl = (baseUrls, appDefinitionId, fetchDevCenterWidgetIds, { moduleVersion = BLOCKS_APP_DESCRIPTOR_SITE_ASSET_MODULE_VERSION, dataFixerVersion = DATA_FIXER_VERSION, } = {}) => {
    const { siteId, metaSiteId, revision: siteRevision, } = parseBaseUrlsData(baseUrls);
    const paramsObj = Object.fromEntries(Object.entries({
        siteId,
        metaSiteId,
        siteRevision,
        moduleVersion,
        dfVersion: dataFixerVersion,
        pageCompId: 'masterPage',
        module: 'blocks-app-descriptor',
        keepWixCodeConnections: false,
        isEditorMode: true,
        viewMode: 'desktop',
        ck: 6,
        isHttps: false,
        isUrlMigrated: true,
        quickActionsMenuEnabled: true,
        appDefinitionId,
        fetchDevCenterWidgetIds,
        v: 3,
    }).map(([key, value]) => [key, value?.toString()]));
    const queryParams = new URLSearchParams(paramsObj);
    return `${BLOCKS_APP_DESCRIPTOR_SITE_ASSET_MODULE_BASE_URL}?${queryParams}`;
};
export class RemoteStructureFetcherError extends Error {
    constructor(message, requestId, body) {
        super(message);
        this.requestId = requestId;
        this.name = 'RemoteStructureFetcherError';
        /* istanbul ignore next */
        this.body = typeof body === 'string' ? body.slice(0, 10240) : body;
    }
}
const fetchDS = (siteHeaderUrl, resource, options) => {
    const responsePromise = fetch(`${dsServerUrl(siteHeaderUrl, options.useAbsolutePath)}${resource}`, {
        ...(options.userSession
            ? { headers: { cookie: `wixSession2=${options.userSession}` } }
            : {}),
        credentials: 'include',
    });
    return responsePromise.then((res) => {
        if (res.ok) {
            return res.json();
        }
        const requestId = res.headers.get('x-wix-request-id') || undefined;
        return res
            .text()
            .catch(() => {
            throw new RemoteStructureFetcherError(`Remote structure fetcher has failed with status ${res.status}`, requestId);
        })
            .then((body) => {
            throw new RemoteStructureFetcherError(`Remote structure fetcher has failed with status ${res.status}`, requestId, body);
        });
    });
};
const getWidgetId = ({ rootCompId }) => rootCompId[0] === '#' ? rootCompId.slice(1) : rootCompId;
export const getWidgetStructure = (siteHeaderUrl, widgetId, options = {}) => fetchDS(siteHeaderUrl, `/widget/${widgetId}`, options);
export const getWidgetStructureByAppData = (appData, widgetPageId, variationId, options = {}) => {
    const { appDefinitionId, widgets } = appData;
    const siteHeaderUrl = getSiteHeaderUrl(appData);
    if (!siteHeaderUrl) {
        throw new Error('siteHeaderUrl was not found');
    }
    const devCenterWidgetId = Object.keys(widgets).find((key) => widgets[key].componentFields?.appStudioFields?.id === widgetPageId);
    const createVariationIdParam = () => `&variationId=${variationId}`;
    const url = `/app_widget/${widgetPageId}?appDefinitionId=${appDefinitionId}&devCenterWidgetId=${devCenterWidgetId}${variationId ? createVariationIdParam() : ''}`;
    return fetchDS(siteHeaderUrl, url, options).then(({ APP_WIDGET }) => APP_WIDGET);
};
/**
 * @description Fetches the appDescriptor from DS Server
 * @returns An object with widgets property, keys are widgetId, value is widget descriptor
 */
export const getAppDescriptor = (siteHeaderUrl, options = {}) => fetchDS(siteHeaderUrl, '/app', options).then((app) => {
    const { APP_DESCRIPTOR } = app;
    const appDescriptor = Array.isArray(APP_DESCRIPTOR)
        ? { widgets: APP_DESCRIPTOR }
        : APP_DESCRIPTOR;
    const widgets = appDescriptor.widgets
        ? appDescriptor.widgets.reduce((acc, value) => {
            acc[getWidgetId(value)] = value;
            return acc;
        }, {})
        : {};
    const panels = Object.values(widgets).reduce((acc, widget) => ({ ...acc, ...widget.panels }), {});
    return {
        ...appDescriptor,
        panels,
        widgets,
        languages: appDescriptor.languages,
        customDefinitions: Object.assign({}, ...(appDescriptor.customDefinitions || [])),
    };
});
const getAppDescriptorFromSiteAssets = async (appData, { httpClient, moduleVersion, dataFixerVersion, fetchDevCenterWidgetIds, editorSDK, }) => {
    const baseUrls = appData.appFields.platform?.baseUrls;
    if (!baseUrls) {
        console.warn('app has no baseUrls field, falling back to DSS', appData);
        return getAppDescriptor(getSiteHeaderUrl(appData));
    }
    if (editorSDK) {
        const [_dataFixerVersion, _moduleVersion] = await Promise.all([
            editorSDK.info.getSiteAssetsScriptVersion(token, {
                scriptName: SANTA_DATA_FIXER,
            }),
            editorSDK.info.getSiteAssetsScriptVersion(token, {
                scriptName: BLOCKS_APP_DESCRIPTOR,
            }),
        ]);
        moduleVersion = _moduleVersion;
        dataFixerVersion = _dataFixerVersion;
    }
    const appDescriptorSiteAssetModuleUrl = getAppDescriptorSiteAssetModuleUrl(baseUrls, appData.appDefinitionId, fetchDevCenterWidgetIds ?? false, {
        moduleVersion,
        dataFixerVersion,
    });
    const appDescriptor = await httpClient
        .get(appDescriptorSiteAssetModuleUrl, {
        headers: {
            'x-wix-extended-timeout': '1',
        },
    })
        .then(({ data }) => data.APP_DESCRIPTOR);
    const widgets = keyBy(appDescriptor.widgets, getWidgetId);
    const panels = Object.values(widgets).reduce((acc, widget) => ({ ...acc, ...widget.panels }), {});
    return {
        ...appDescriptor,
        panels,
        widgets,
        customDefinitions: Object.assign({}, ...(appDescriptor.customDefinitions || [])),
    };
};
export const getSiteHeaderUrl = (appData) => {
    const result = appData.appFields.platform?.baseUrls?.siteHeaderUrl ||
        appData.appFields.platform?.studio?.siteHeaderUrl;
    if (!result) {
        throw new Error('siteHeaderUrl was not found');
    }
    return result;
};
export const getAppLanguages = (appData, options = {}) => {
    return fetchDS(getSiteHeaderUrl(appData), '/blocks-languages', options);
};
export async function getAppDescriptorByAppData(appData, options) {
    if (options) {
        try {
            return await getAppDescriptorFromSiteAssets(appData, options);
        }
        catch (e) {
            console.error('Failed to fetch app descriptor from site assets', e);
            options.onSiteAssetsFetchError?.(e);
            return getAppDescriptor(getSiteHeaderUrl(appData));
        }
    }
    else {
        return getAppDescriptor(getSiteHeaderUrl(appData));
    }
}
//# sourceMappingURL=remoteStructureFetcher.js.map